import { useState, useCallback, useEffect } from 'react';

const TREE_STATE_KEY = 'family-tree-expanded-nodes';

export interface TreeState {
    expandedNodes: Set<number>;
    toggleNode: (id: number) => void;
    expandNode: (id: number) => void;
    collapseNode: (id: number) => void;
    expandAll: () => void;
    collapseAll: () => void;
    isExpanded: (id: number) => boolean;
}

export function useTreeState(rootId: number, allIds: number[]): TreeState {
    const [expandedNodes, setExpandedNodes] = useState<Set<number>>(() => {
        // Try to load from session storage
        try {
            const stored = sessionStorage.getItem(TREE_STATE_KEY);
            if (stored) {
                const parsed = JSON.parse(stored);
                if (Array.isArray(parsed)) {
                    return new Set(parsed);
                }
            }
        } catch {
            // Ignore storage errors
        }
        // Default: expand root node only
        return new Set([rootId]);
    });

    // Save to session storage when state changes
    useEffect(() => {
        try {
            sessionStorage.setItem(
                TREE_STATE_KEY,
                JSON.stringify([...expandedNodes])
            );
        } catch {
            // Ignore storage errors
        }
    }, [expandedNodes]);

    const toggleNode = useCallback((id: number) => {
        setExpandedNodes(prev => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    }, []);

    const expandNode = useCallback((id: number) => {
        setExpandedNodes(prev => {
            if (prev.has(id)) return prev;
            const next = new Set(prev);
            next.add(id);
            return next;
        });
    }, []);

    const collapseNode = useCallback((id: number) => {
        setExpandedNodes(prev => {
            if (!prev.has(id)) return prev;
            const next = new Set(prev);
            next.delete(id);
            return next;
        });
    }, []);

    const expandAll = useCallback(() => {
        setExpandedNodes(new Set(allIds));
    }, [allIds]);

    const collapseAll = useCallback(() => {
        // Keep root expanded
        setExpandedNodes(new Set([rootId]));
    }, [rootId]);

    const isExpanded = useCallback((id: number) => {
        return expandedNodes.has(id);
    }, [expandedNodes]);

    return {
        expandedNodes,
        toggleNode,
        expandNode,
        collapseNode,
        expandAll,
        collapseAll,
        isExpanded,
    };
}
